import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  EditIcon,
  FileSignatureIcon,
  FileTextIcon,
  MailIcon,
  MapIcon,
  PercentIcon,
  PhoneIcon,
  TrashIcon,
  UserCogIcon,
  UserIcon,
} from 'lucide-react'
import ViewSignaturePage from '@/features/signatures/components/ViewSignaturePage'
import { getStatusColor } from '@/features/products/utils'
import { formatNome } from '@/utils/format'
import { TypeCargo } from '@/lib/enums'
import type { User } from '@/types/user'

interface InfoUsuarioHeaderProps {
  user: User
  initials: string
  displayName: string
  canDelete: boolean
  isDeleting: boolean
  onViewMinute: () => void
  onDelete: () => void
}

interface InfoRowProps {
  label: string
  value: ReactNode
  icon?: ReactNode
  className?: string
}

interface InfoUsuarioPersonalSectionProps {
  user: User
  displayName: string
  createdDateDisplay: string
}

interface InfoUsuarioRelationshipsSectionProps {
  createdByDisplay: string
  responsibleDisplay: string
}

interface InfoUsuarioProfessionalSectionProps {
  user: User
  isEditingCargo: boolean
  isEditingComissao: boolean
  cargoForm: string
  comissaoForm: string
  onEditCargo: () => void
  onSaveCargo: () => void
  onCancelCargo: () => void
  onCargoChange: (value: string) => void
  onEditComissao: () => void
  onSaveComissao: () => void
  onCancelComissao: () => void
  onComissaoChange: (value: string) => void
}

function InfoRow({ label, value, icon, className }: InfoRowProps) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        {icon}
        {label}
      </label>
      <p className="text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  )
}

export function InfoUsuarioHeader({
  user,
  initials,
  displayName,
  canDelete,
  isDeleting,
  onViewMinute,
  onDelete,
}: InfoUsuarioHeaderProps) {
  return (
    <Card className="border-0 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm dark:from-blue-950/20 dark:to-indigo-950/20">
      <CardContent className="pt-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-hover text-xl font-bold text-foreground-secondary">
              {initials}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{displayName}</h1>
              <p className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <MailIcon className="h-4 w-4" />
                {user.email}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <Badge variant="outline" className={getStatusColor(user?.status)}>
                  {user?.status ?? '—'}
                </Badge>
                <Badge variant="outline">{user?.typeUser ?? '—'}</Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onViewMinute} className="gap-2 bg-transparent">
              <FileTextIcon className="h-4 w-4" />
              Ver Minuta
            </Button>
            {canDelete ? (
              <Button
                variant="outline"
                onClick={onDelete}
                disabled={isDeleting}
                className="gap-2 bg-transparent text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4" />
                {isDeleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioPersonalSection({
  user,
  displayName,
  createdDateDisplay,
}: InfoUsuarioPersonalSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserIcon className="h-5 w-5" />
          Informações Pessoais
        </CardTitle>
        <CardDescription>Dados pessoais cadastrados no sistema</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InfoRow label="Nome Completo" value={<span className="font-medium">{displayName}</span>} />
          <InfoRow label="CPF" value={user.cpf || 'Não informado'} />
          <InfoRow label="Email" value={user.email || 'Não informado'} icon={<MailIcon className="h-4 w-4" />} />
          <InfoRow
            label="WhatsApp"
            value={user.whatsApp || 'Não informado'}
            icon={<PhoneIcon className="h-4 w-4" />}
          />
          <InfoRow
            label="Data de Cadastro"
            value={createdDateDisplay}
            icon={<CalendarIcon className="h-4 w-4" />}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioRelationshipsSection({
  createdByDisplay,
  responsibleDisplay,
}: InfoUsuarioRelationshipsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCogIcon className="h-5 w-5" />
          Rastreabilidade e Responsáveis
        </CardTitle>
        <CardDescription>
          Exibe quem criou o usuário e vínculos de responsabilidade quando essas informações vierem da API.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InfoRow label="Criado por" value={createdByDisplay} />
          <InfoRow label="Responsável" value={responsibleDisplay} />
        </div>
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioProfessionalSection({
  user,
  isEditingCargo,
  isEditingComissao,
  cargoForm,
  comissaoForm,
  onEditCargo,
  onSaveCargo,
  onCancelCargo,
  onCargoChange,
  onEditComissao,
  onSaveComissao,
  onCancelComissao,
  onComissaoChange,
}: InfoUsuarioProfessionalSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BriefcaseIcon className="h-5 w-5" />
          Cargo e Comissão
        </CardTitle>
        <CardDescription>Informações profissionais e comissão do usuário</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargo</label>

            {!isEditingCargo ? (
              <Button variant="ghost" size="sm" onClick={onEditCargo} className="h-8 gap-2 px-3">
                <EditIcon className="h-3 w-3" />
                Editar
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" onClick={onSaveCargo} className="h-8 px-3">
                  Salvar
                </Button>
                <Button size="sm" variant="outline" onClick={onCancelCargo} className="h-8 px-3">
                  Cancelar
                </Button>
              </div>
            )}
          </div>

          {!isEditingCargo ? (
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
              <p className="font-medium text-gray-900 dark:text-gray-100">{formatNome(user.typeCargo)}</p>
            </div>
          ) : (
            <select
              className="w-full rounded-md border bg-white px-3 py-2 dark:bg-gray-900"
              value={cargoForm}
              onChange={event => onCargoChange(event.target.value)}
            >
              <option value="">Selecione…</option>
              <option value={TypeCargo.CEO}>CEO</option>
              <option value={TypeCargo.PARCEIRO}>Parceiro Comercial</option>
              <option value={TypeCargo.GERENTE}>Parceiro Gestor</option>
            </select>
          )}
        </div>

        {user.comissao_user != null ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <PercentIcon className="h-4 w-4" />
                Comissão
              </label>

              {!isEditingComissao ? (
                <Button variant="ghost" size="sm" onClick={onEditComissao} className="h-8 gap-2 px-3">
                  <EditIcon className="h-3 w-3" />
                  Alterar
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" onClick={onSaveComissao} className="h-8 px-3">
                    Salvar
                  </Button>
                  <Button size="sm" variant="outline" onClick={onCancelComissao} className="h-8 px-3">
                    Cancelar
                  </Button>
                </div>
              )}
            </div>

            {!isEditingComissao ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
                <p className="text-lg font-bold text-green-800 dark:text-green-200">{user.comissao_user}%</p>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  className="w-40 rounded-md border bg-white px-3 py-2 dark:bg-gray-900"
                  value={comissaoForm}
                  onChange={event => onComissaoChange(event.target.value)}
                  placeholder="Ex.: 12"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioAddressSection({ user }: { user: User }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapIcon className="h-5 w-5" />
          Endereço
        </CardTitle>
        <CardDescription>Endereço cadastrado no sistema</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="CEP" value={user.cep || 'Não informado'} />
          <InfoRow label="Logradouro" value={user.logradouro || 'Não informado'} className="md:col-span-2" />
          <InfoRow label="Número" value={user.numero || 'Não informado'} />
          <InfoRow label="Complemento" value={user.complemento || 'Não informado'} />
          <InfoRow label="Bairro" value={user.bairro || 'Não informado'} />
          <InfoRow label="Município" value={user.municipio || 'Não informado'} />
          <InfoRow label="Estado" value={user.estado || 'Não informado'} />
        </div>
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioAccountingSection({ user }: { user: User }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BuildingIcon className="h-5 w-5" />
          Informações de Contabilidade
        </CardTitle>
        <CardDescription>Dados específicos para usuários de contabilidade</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InfoRow label="Telefone" value={user.tel || 'Não informado'} icon={<PhoneIcon className="h-4 w-4" />} />
          <InfoRow label="CNPJ" value={user.cnpj || 'Não informado'} />
          <InfoRow label="Razão Social" value={user.razao_social || 'Não informado'} className="md:col-span-2" />
        </div>
      </CardContent>
    </Card>
  )
}

export function InfoUsuarioSignatureSection({ user }: { user: User }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignatureIcon className="h-5 w-5" />
          Assinatura Digital
        </CardTitle>
        <CardDescription>Verificação e gerenciamento de assinatura digital</CardDescription>
      </CardHeader>
      <CardContent>
        <ViewSignaturePage id={user.id || ''} role="user" />
      </CardContent>
    </Card>
  )
}
